import {RichTextEditor, type RichTextEditorFunctionsType} from '@forecast-it/design-system';
import React, {useCallback, useRef} from 'react';
import {useDraftJsHtmlConversion} from './useDraftJsConversion';
import {type EditorSurface} from '../../../../tracking/amplitude/editorEvents';
import {RichTextEditorProps} from '@forecast-it/design-system/dist/forecast-v2/components/inputs/richTextEditor/RichTextEditor';

type RichTextEditorDraftJsWrapperProps = RichTextEditorProps & {
	peopleMentions?: {id: string; text: string}[];
	initialDraftJsContent: string;
	onDraftJsChanged?: (string) => void;
	'data-cy'?: string;
	id?: string;
	/**
	 * `React.Ref`, not `RefObject`: two consumers pass a callback here
	 * (comment_footer_section, comment_section_comment), which the narrower type had
	 * always been lying about.
	 */
	editorRef?: React.Ref<RichTextEditorFunctionsType>;
	readOnly?: boolean;
	maxHeight?: string;
	placeholder?: string;
	/**
	 * Which editor this is, for analytics. Editors outside the task modal leave it
	 * unset and raise no events — an event that cannot say which surface it came from
	 * is not actionable.
	 */
	surface?: EditorSurface;
};

export const RichTextEditorDraftJsWrapper = ({
	peopleMentions,
	initialDraftJsContent,
	onDraftJsChanged,
	'data-cy': dataCy,
	id = 'editor',
	editorRef,
	readOnly,
	maxHeight,
	placeholder,
	surface,
	...rest
}: RichTextEditorDraftJsWrapperProps): React.JSX.Element => {
	// The wrapper needs the editor handle for itself — the conversion hook reads the
	// editor's own serialisation through it, to tell a blur that saved something from a
	// click through the field — so the caller's ref is attached alongside rather than
	// instead.
	const editorHandle = useRef<RichTextEditorFunctionsType | null>(null);
	const attachEditor = useCallback(
		(instance: RichTextEditorFunctionsType | null) => {
			editorHandle.current = instance;
			if (typeof editorRef === 'function') {
				editorRef(instance);
			} else if (editorRef) {
				(editorRef as React.MutableRefObject<RichTextEditorFunctionsType | null>).current = instance;
			}
		},
		[editorRef]
	);
	// Stable identity: the hook captures the baseline in a mount effect keyed on this,
	// and the description rebinds its save callback on every render.
	const getEditorHtml = useCallback(() => editorHandle.current?.getHtmlValue(), []);

	const {initialHtml, convertHtmlToDraftJs} = useDraftJsHtmlConversion({
		initialDraftJsContent,
		onDraftJsChanged,
		surface,
		getEditorHtml,
	});
	return (
		<RichTextEditor
			id={id}
			data-cy={dataCy}
			peopleMentions={peopleMentions}
			onBlur={convertHtmlToDraftJs}
			initialHtml={initialHtml}
			ref={attachEditor}
			readOnly={readOnly}
			maxHeight={maxHeight}
			placeholder={placeholder}
			{...rest}
		/>
	);
};
